import React from 'react';
import { CheckCircle, Shield, Clock, Heart } from 'lucide-react';

const About = () => {
  const scrollToQuote = () => {
    const element = document.querySelector('#quote');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Respect for Your Property",
      description: "We live by a simple rule: leave it better than we found it. From shoe covers to spotless cleanup, we treat your home with the respect it deserves."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Quick & Easy Quotes",
      description: "We don't keep you waiting. You'll have a clear, fair estimate in your inbox within 1 business day — no hassle, no pressure."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Fully Insured",
      description: "No need to stress — your property is always protected. We carry full liability and workers' comp so you can relax knowing we've got it covered."
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: "We're Reliable",
      description: "Your time matters. We show up when we say we will, work with care, and if something's not right, we'll make it right — personally."
    }
  ];

  return (
    <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            What Sets Us Apart
          </h2>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
            <p>
              As a locally owned, family-operated company serving the Hamptons and greater Long Island, this isn't just where we work — it's where we live. We understand the coastal elements, the salt air, the seasonal shifts, and the level of excellence luxury homes require. That's why every property we service is treated with precision, respect, and an uncompromising standard of quality.
            </p>
            <p>
              Our team is professional, fully insured, and trained in safe, proven methods that protect your home while delivering exceptional results. More importantly, we operate with integrity. When we step onto your property, you can expect discretion, reliability, and craftsmanship.
            </p>
            <p>
              Choosing <span className="text-gradient-reflections font-semibold">Reflections Cleanings</span> means choosing peace of mind. It means choosing a company that stands behind its work and values long-term relationships over one-time jobs.
            </p>
            <p>
              Because to us, it's not just about cleaning — it's about helping your home reflect its very best.
            </p>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <button
              onClick={scrollToQuote}
              className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Request Quote — Within 1 Business Day
            </button>
            <p className="mt-4 text-sm text-gray-600">
              No hassle. No obligation. Just a clear, fair quote within 24 hours.
            </p>
          </div>
        </div>

        {/* "We don't just clean — we care" Section Header */}
        <div className="text-center mb-12">
          <h3 className="text-3xl sm:text-4xl font-bold mb-2">
            <span className="text-gradient-reflections">Reflections Cleanings</span>
          </h3>
          <p className="text-xl text-gray-600">
            We don't just clean — we care
          </p>
        </div>

        {/* Combined Features Grid - 4 columns like screenshot */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white border-2 border-blue-400 rounded-2xl p-6 text-center hover:shadow-lg transition-all duration-300"
              data-testid={`feature-card-${index}`}
            >
              {/* Icon */}
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="text-white">
                  {feature.icon}
                </div>
              </div>
              
              {/* Title */}
              <h4 className="text-lg font-bold text-gray-900 mb-3 italic">
                {feature.title}
              </h4>
              
              {/* Description */}
              <p className="text-gray-600 text-sm leading-relaxed italic">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
